import { useEffect, useMemo, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { IoClose } from "react-icons/io5";

const abrirDropdown = keyframes`
    from {
        opacity: 0;
        transform: translateY(-10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const FILTER_FIELD_FONT_SIZE = "14px";
const FILTER_BORDER_COLOR = "rgba(0, 0, 0, 0.2)";
const FILTER_BORDER_COLOR_HOVER = "rgba(0, 0, 0, 0.35)";
const FILTER_BORDER_COLOR_FOCUS = "#007bff";
const FILTER_BORDER_RADIUS = "8px";
const FILTER_FOCUS_SHADOW = "0 0 0 2px rgba(0, 123, 255, 0.15)";

const Container = styled.div`
    box-sizing: border-box;
    border: 1px solid
        ${(props) => (props.isOpen ? FILTER_BORDER_COLOR_FOCUS : FILTER_BORDER_COLOR)};
    box-shadow: ${(props) => (props.isOpen ? FILTER_FOCUS_SHADOW : "none")};
    display: flex;
    align-items: center;
    background-color: #fff;
    padding: 6px 10px;
    border-radius: ${FILTER_BORDER_RADIUS};
    font-size: ${FILTER_FIELD_FONT_SIZE};
    font-family: inherit;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;

    &:hover {
        border-color: ${(props) =>
            props.isOpen ? FILTER_BORDER_COLOR_FOCUS : FILTER_BORDER_COLOR_HOVER};
    }
    position: relative;
    width: ${(props) => (props.largura ? props.largura : "100%")};
    height: 38px;
`;

const ContainerPlaceholder = styled.div`
    display: flex;
    align-items: center;
    height: 100%;
    flex: 1;
    min-width: 0;
    cursor: pointer;
`;

const Placeholder = styled.p`
    margin: 0;
    padding: 0;
    font-size: ${FILTER_FIELD_FONT_SIZE};
    color: #666;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const ContainerIconeClose = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 18px;
    width: 18px;
    margin: auto auto auto 5px;
`;

const IconeClose = styled(IoClose)`
    width: 100%;
    height: 100%;
    color: #666;
    cursor: pointer;
    &:hover {
        color: #333;
    }
    margin: 0;
    padding: 0;
    box-sizing: border-box;
`;

const IconeSeta = styled.span`
    border: solid #666;
    border-width: 0 1.5px 1.5px 0;
    transform: rotate(45deg);
    padding: 2.5px;
    margin: 0 6px 5px auto;
    flex-shrink: 0;
    cursor: pointer;
`;

const Dropdown = styled.div`
    position: absolute;
    background-color: #fff;
    border: 1px solid ${FILTER_BORDER_COLOR};
    border-radius: ${FILTER_BORDER_RADIUS};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
    top: 39px;
    left: 0;
    z-index: 1000;
    min-width: 100%;
    max-height: 220px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    animation: ${abrirDropdown} 0.2s ease-out;
`;

const SearchWrap = styled.div`
    flex-shrink: 0;
    padding: 8px;
    border-bottom: 1px solid #eee;
    background-color: #fff;
`;

const SearchInput = styled.input`
    width: 100%;
    height: 32px;
    box-sizing: border-box;
    border: 1px solid ${FILTER_BORDER_COLOR};
    border-radius: 6px;
    padding: 0 10px;
    font-size: ${FILTER_FIELD_FONT_SIZE};
    font-family: inherit;
    color: #333;
    background-color: #fff;

    &::placeholder {
        color: #666;
        font-size: ${FILTER_FIELD_FONT_SIZE};
    }

    &:focus {
        outline: none;
        border-color: ${FILTER_BORDER_COLOR_FOCUS};
        box-shadow: ${FILTER_FOCUS_SHADOW};
    }
`;

const OptionsList = styled.div`
    overflow-y: auto;
    max-height: 172px;

    &::-webkit-scrollbar {
        width: 8px;
    }

    &::-webkit-scrollbar-thumb {
        background-color: #c1c1c1;
        border-radius: 8px;
    }
`;

const Option = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    padding: 7px 10px;
    box-sizing: border-box;
    font-size: ${FILTER_FIELD_FONT_SIZE};
    color: #333;
    cursor: pointer;
    border-bottom: 1px solid #eee;

    &:last-child {
        border-bottom: none;
    }

    &:hover {
        background-color: #f5f5f5;
    }

    background-color: ${(props) => (props.selected ? "#e6f7ff" : "transparent")};
`;

const EmptyMessage = styled.p`
    margin: 0;
    padding: 12px 10px;
    font-size: ${FILTER_FIELD_FONT_SIZE};
    color: #666;
    text-align: center;
`;

const OptionCheckbox = styled.input`
    margin-right: 10px;
    flex-shrink: 0;
`;

const MultiselectCheck = ({
    options,
    selectedOptions,
    setSelectedOptions,
    placeholder,
    largura,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    const containerRef = useRef(null);
    const searchInputRef = useRef(null);

    const filteredOptions = useMemo(() => {
        const term = searchTerm.trim().toUpperCase();
        if (!term) return options;

        return options.filter((option) => {
            const label = String(option.label ?? option.value ?? "").toUpperCase();
            return label.includes(term);
        });
    }, [options, searchTerm]);

    const allFilteredSelected =
        filteredOptions.length > 0 &&
        filteredOptions.every((option) => selectedOptions.includes(option.value));

    const showTodosChecked =
        searchTerm.trim() === ""
            ? selectedOptions.length === 0 || selectedOptions.length === options.length
            : allFilteredSelected;

    useEffect(() => {
        function handleClickOutside(e) {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("touchstart", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("touchstart", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (isOpen) {
            requestAnimationFrame(() => {
                searchInputRef.current?.focus();
            });
        } else {
            setSearchTerm("");
        }
    }, [isOpen]);

    const toggleOption = (optionValue) => {
        if (selectedOptions.includes(optionValue)) {
            setSelectedOptions(selectedOptions.filter((opt) => opt !== optionValue));
        } else {
            setSelectedOptions([...selectedOptions, optionValue]);
        }
    };

    const isOptionSelected = (optionValue) => selectedOptions.includes(optionValue);

    const toggleOpen = () => {
        setIsOpen((prev) => !prev);
    };

    const dropdownClick = (e) => {
        e.stopPropagation();
    };

    const selectAll = () => {
        if (searchTerm.trim() === "") {
            if (selectedOptions.length === options.length) {
                setSelectedOptions([]);
            } else {
                setSelectedOptions(options.map((option) => option.value));
            }
            return;
        }

        const filteredValues = filteredOptions.map((option) => option.value);

        if (allFilteredSelected) {
            setSelectedOptions(
                selectedOptions.filter((value) => !filteredValues.includes(value))
            );
        } else {
            const merged = new Set([...selectedOptions, ...filteredValues]);
            setSelectedOptions([...merged]);
        }
    };

    return (
        <Container ref={containerRef} isOpen={isOpen} largura={largura}>
            <ContainerPlaceholder onClick={toggleOpen}>
                <Placeholder>
                    {selectedOptions.length === 0
                        ? placeholder || "TODOS"
                        : `${selectedOptions.length} SELECIONADO${selectedOptions.length > 1 ? "S" : ""}`}
                </Placeholder>
            </ContainerPlaceholder>

            {selectedOptions.length > 0 && (
                <ContainerIconeClose>
                    <IconeClose
                        onClick={(e) => {
                            e.stopPropagation();
                            setSelectedOptions([]);
                        }}
                    />
                </ContainerIconeClose>
            )}

            <IconeSeta onClick={toggleOpen} />

            {isOpen && (
                <Dropdown onClick={dropdownClick}>
                    <SearchWrap>
                        <SearchInput
                            ref={searchInputRef}
                            type="text"
                            value={searchTerm}
                            placeholder="PESQUISAR..."
                            onChange={(e) => setSearchTerm(e.target.value.toUpperCase())}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                        />
                    </SearchWrap>

                    <OptionsList>
                        <Option selected={showTodosChecked} onClick={selectAll}>
                            <OptionCheckbox type="checkbox" checked={showTodosChecked} readOnly />
                            TODOS
                        </Option>

                        {filteredOptions.length === 0 ? (
                            <EmptyMessage>NENHUMA OPÇÃO ENCONTRADA</EmptyMessage>
                        ) : (
                            filteredOptions.map((option) => (
                                <Option
                                    key={option.value}
                                    selected={isOptionSelected(option.value)}
                                    onClick={() => toggleOption(option.value)}
                                >
                                    <OptionCheckbox
                                        type="checkbox"
                                        checked={isOptionSelected(option.value)}
                                        readOnly
                                    />
                                    {option.label}
                                </Option>
                            ))
                        )}
                    </OptionsList>
                </Dropdown>
            )}
        </Container>
    );
};

export default MultiselectCheck;
